import React, { useState } from "react";
import Button from "@mui/material/Button";
import { Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import { BaseURL } from "../../../Hooks/URL";

const FileAttachmentInput = ({
  existingAttachments = [],
  newAttachments = [],
  deletedAttachmentIds = [],
  onAttachmentUpload,
  onRemoveAttachment,
  onUpdateDeletedIds,
  onUpdateExistingAttachments,
  onUpdateNewAttachments,
  maxFiles = 5,
  label = "File Attachments:",
  accept = "*/*",
  disabled = false,
  baseURL = BaseURL,
}) => {
  const [renamingIndex, setRenamingIndex] = useState(null);
  const [newFileName, setNewFileName] = useState("");

  const handleAttachmentUpload = (e) => {
    const files = Array.from(e.target.files);
    const allAttachments = [...existingAttachments, ...newAttachments];

    // Check if adding these files would exceed the limit
    if (allAttachments.length + files.length > maxFiles) {
      alert(
        `Maximum ${maxFiles} files allowed. Please remove some files before adding new ones.`
      );
      return;
    }

    // Check file size (10 MB = 10 * 1024 * 1024 bytes)
    const maxFileSize = 30 * 1024 * 1024; // 30 MB
    const oversizedFiles = files.filter(file => file.size > maxFileSize);
    
    if (oversizedFiles.length > 0) {
      const oversizedFileNames = oversizedFiles.map(file => file.name).join(', ');
      alert(
        `The following files exceed the 10 MB size limit and will be skipped:\n${oversizedFileNames}`
      );
      
      // Only process files that are within the size limit
      const validFiles = files.filter(file => file.size <= maxFileSize);
      if (validFiles.length > 0) {
        onAttachmentUpload(validFiles);
      }
    } else {
      onAttachmentUpload(files);
    }
    
    e.target.value = ""; // Reset the input
  };

  const removeAttachment = (index, isExisting = false) => {
    if (isExisting) {
      // Remove from existing attachments and add ID to deleted list
      const attachmentToRemove = existingAttachments[index];
      if (attachmentToRemove.id !== undefined) {
        onUpdateDeletedIds([...deletedAttachmentIds, attachmentToRemove.id]);
      }
      onRemoveAttachment(index, true);
    } else {
      // Remove from new attachments
      onRemoveAttachment(index, false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // File rename handlers
  const handleRenameStart = (index, currentName, isExisting = false) => {
    setRenamingIndex({ index, isExisting });
    // Extract filename without extension for editing
    const lastDotIndex = currentName.lastIndexOf(".");
    const nameWithoutExtension =
      lastDotIndex > 0 ? currentName.substring(0, lastDotIndex) : currentName;
    setNewFileName(nameWithoutExtension);
  };

  const handleRenameSave = () => {
    if (newFileName.trim() && renamingIndex) {
      const { index, isExisting } = renamingIndex;

      if (isExisting) {
        const updatedAttachments = [...existingAttachments];
        const originalName = existingAttachments[index].name;

        // Get the original file extension
        const lastDotIndex = originalName.lastIndexOf(".");
        const extension =
          lastDotIndex > 0 ? originalName.substring(lastDotIndex) : "";

        // Combine new name with original extension
        const newFullName = newFileName.trim() + extension;

        updatedAttachments[index] = {
          ...updatedAttachments[index],
          name: newFullName,
        };

        if (onUpdateExistingAttachments) {
          onUpdateExistingAttachments(updatedAttachments);
        }
      } else {
        const updatedAttachments = [...newAttachments];
        const originalName = newAttachments[index].name;

        // Get the original file extension
        const lastDotIndex = originalName.lastIndexOf(".");
        const extension =
          lastDotIndex > 0 ? originalName.substring(lastDotIndex) : "";

        // Combine new name with original extension
        const newFullName = newFileName.trim() + extension;

        updatedAttachments[index] = {
          ...updatedAttachments[index],
          name: newFullName,
        };

        if (onUpdateNewAttachments) {
          onUpdateNewAttachments(updatedAttachments);
        }
      }
    }
    setRenamingIndex(null);
    setNewFileName("");
  };

  const handleRenameCancel = () => {
    setRenamingIndex(null);
    setNewFileName("");
  };

  // File download handler
  const handleDownload = (attachment) => {
    // Create a temporary URL for the file
    const url = attachment.id
      ? `${baseURL}/${attachment.file}`
      : URL.createObjectURL(attachment.file);

    // Create a temporary anchor element to trigger download
    const link = document.createElement("a");
    link.target = "_blank";
    link.href = url;
    link.download = attachment.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the object URL if it was created
    if (!attachment.id) {
      URL.revokeObjectURL(url);
    }
  };

  const allAttachments = [...existingAttachments, ...newAttachments];

  return (
    <div style={{ marginBottom: "20px" }}>
      <label className="aboutLabel" style={{ marginTop: "20px" }}>
        {label}
      </label>

      <Button
        component="label"
        variant="outlined"
        startIcon={<AttachFileIcon />}
        style={{
          marginTop: "0",
        }}
        disabled={disabled || allAttachments.length >= maxFiles}
        className="actionButton primary addButton"
      >
        Upload Files (Max {maxFiles})
        <input
          type="file"
          multiple
          hidden
          onChange={handleAttachmentUpload}
          accept={accept}
        />
      </Button>

      {allAttachments.length > 0 && (
        <div style={{ marginTop: "10px" }}>
          <Typography
            style={{
              fontSize: "14px",
              color: "#666",
              marginBottom: "8px",
            }}
          >
            Attached Files ({allAttachments.length}/{maxFiles}):
          </Typography>

          {allAttachments.map((attachment, index) => {
            const isExisting = attachment.isExisting || false;
            const actualIndex = isExisting
              ? index
              : index - existingAttachments.length;
            const isRenaming =
              renamingIndex?.index === actualIndex &&
              renamingIndex?.isExisting === isExisting;

            // Dynamic styles based on whether it's existing or new
            const backgroundColor = isExisting ? "#e8f5e8" : "#f5f5f5";
            const borderColor = isExisting ? "#4caf50" : "#ddd";
            const iconColor = isExisting ? "#4caf50" : "#666";
            const sizeText = isExisting
              ? "Existing"
              : `${formatFileSize(attachment.size)} • New`;

            return (
              <div
                key={`${isExisting ? "existing" : "new"}-${actualIndex}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  backgroundColor: backgroundColor,
                  borderRadius: "4px",
                  marginBottom: "8px",
                  border: `1px solid ${borderColor}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <AttachFileIcon
                    style={{
                      marginRight: "8px",
                      color: iconColor,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    {isRenaming ? (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                        }}
                      >
                        <input
                          type="text"
                          value={newFileName}
                          onChange={(e) => setNewFileName(e.target.value)}
                          style={{
                            flex: 0.9,
                            padding: "4px 8px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                            fontSize: "14px",
                            outline: "none",
                          }}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleRenameSave();
                            } else if (e.key === "Escape") {
                              handleRenameCancel();
                            }
                          }}
                          autoFocus
                        />
                        {!isExisting && (
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#666",
                              fontWeight: "500",
                            }}
                          >
                            {(() => {
                              const lastDotIndex =
                                attachment.name.lastIndexOf(".");
                              return lastDotIndex > 0
                                ? attachment.name.substring(lastDotIndex)
                                : "";
                            })()}
                          </span>
                        )}
                      </div>
                    ) : (
                      <>
                        <Typography
                          style={{
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#333",
                          }}
                        >
                          {attachment.name}
                        </Typography>
                        <Typography
                          style={{
                            fontSize: "12px",
                            color: "#666",
                          }}
                        >
                          {sizeText}
                        </Typography>
                      </>
                    )}
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  {isRenaming ? (
                    <>
                      <Button
                        onClick={handleRenameSave}
                        style={{
                          minWidth: "auto",
                          padding: "4px",
                          color: isExisting ? "#4caf50" : "#2e7d32",
                        }}
                        title="Save changes"
                      >
                        <SaveIcon fontSize="small" />
                      </Button>
                      <Button
                        onClick={handleRenameCancel}
                        style={{
                          minWidth: "auto",
                          padding: "4px",
                          color: "#f44336",
                        }}
                        title="Cancel rename"
                      >
                        <CancelIcon fontSize="small" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="small"
                        onClick={() => handleDownload(attachment)}
                        style={{
                          minWidth: "auto",
                          padding: "4px 8px",
                          color: "#2196f3",
                        }}
                        title="Download file"
                      >
                        <DownloadIcon fontSize="small" />
                      </Button>
                      {!isExisting && (
                        <Button
                          size="small"
                          onClick={() =>
                            handleRenameStart(
                              actualIndex,
                              attachment.name,
                              isExisting
                            )
                          }
                          disabled={renamingIndex !== null}
                          style={{
                            minWidth: "auto",
                            padding: "4px 8px",
                            color: "#666",
                          }}
                          title="Rename file"
                        >
                          <EditIcon fontSize="small" />
                        </Button>
                      )}
                      <Button
                        size="small"
                        onClick={() =>
                          removeAttachment(actualIndex, isExisting)
                        }
                        style={{
                          minWidth: "auto",
                          padding: "4px 8px",
                          color: "#f44336",
                        }}
                        title="Remove file"
                      >
                        <DeleteIcon fontSize="small" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {allAttachments.length >= maxFiles && (
        <Typography
          style={{
            fontSize: "12px",
            color: "#d32f2f",
            marginTop: "5px",
          }}
        >
          Maximum number of files reached ({maxFiles}/{maxFiles})
        </Typography>
      )}
    </div>
  );
};

export default FileAttachmentInput;
